import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { api } from '../../../services/api';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import Tooltip from '../../../components/tooltip';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Container,
  FormContainer,
  Header,
  BackButton,
  Title,
  Form,
  InputGroup,
  Label,
  Input,
  Textarea,
  Select,
  InputRow,
  SubmitButton,
  XPTOContainer,
  XPTOTitle,
  XPTODescription,
  XPTOTextArea,
  XPTOSaveButton,
  FormWrapper
} from './styles';
import {
  DeleteModal as ConfirmModal,
  DeleteModalContent as ConfirmModalContent,
  DeleteModalButtons as ConfirmModalButtons,
  CancelButton
} from '../styles';
import styled, { keyframes } from 'styled-components';
import Loading from '../../../components/Loading';

// Animação para entrada da modal
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Botão verde para confirmar adição/edição
const GreenConfirmButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: ${({ theme }) => theme.success};
  color: ${({ theme }) => theme.successOn};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.success};
  }
`;

// Botão azul para OK na modal de sucesso
const BlueButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
`;

// Modal animada
const AnimatedModal = styled(ConfirmModal)`
  .modal-content {
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

// Modal de conteúdo animada
const AnimatedModalContent = styled(ConfirmModalContent)`
  animation: ${fadeIn} 0.3s ease-out;
`;

const RuleForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { id } = useParams(); // Pega o ID da URL se estiver editando
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successAction, setSuccessAction] = useState('');
  const [useMockData, setUseMockData] = useState(false); // Flag para usar dados mockados
  
  const [xptoInstructions, setXptoInstructions] = useState('');
  const [xptoName, setXptoName] = useState('');

  // Se houver ID, busca os dados da regra
  useEffect(() => {
    const fetchRuleData = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const token = localStorage.getItem('@App:token');
        
        // Verificar se o usuário tem organizationId
        if (!user || !user.organizationId) {
          throw new Error('Nenhuma organização encontrada para o usuário');
        }
        
        // Se organizationId for um array, usa o primeiro
        const organizationId = Array.isArray(user.organizationId) 
          ? user.organizationId[0] 
          : user.organizationId;
        
        const response = await api.get(`/api/prompt/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log('Rule data loaded:', response.data);
        
        const ruleData = response.data;
        
        setXptoName(ruleData.name || '');
        setXptoInstructions(ruleData.prompt || ruleData.description || '');
      } catch (error) {
        console.error('Error loading rule data:', error);
        if (!useMockData) {
          setUseMockData(true);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRuleData();
  }, [id, user, useMockData]);

  const handleXPTOInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setXptoInstructions(e.target.value);
  };

  const handleXPTONameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setXptoName(e.target.value);
  };

  const handleXPTOSubmit = async () => {
    // Validação dos campos
    if (!xptoName.trim()) {
      alert(t('form.rule.ruleNameRequired'));
      return;
    }
    
    if (!xptoInstructions.trim()) {
      alert(t('form.rule.instructionsRequired'));
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Verificar se o usuário tem organizationId
      if (!user || !user.organizationId) {
        throw new Error('Nenhuma organização encontrada para o usuário');
      }
      
      // Se organizationId for um array, usa o primeiro
      const organizationId = Array.isArray(user.organizationId) 
        ? user.organizationId[0] 
        : user.organizationId;
      
      const token = localStorage.getItem('@App:token');
      let response;
      
      if (id) {
        // Se tem ID, é edição
        response = await api.patch(`/api/prompt/${id}`, {
          name: xptoName,
          idOrganization: organizationId,
          prompt: xptoInstructions
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSuccessAction('updated');
      } else {
        // Se não tem ID, é criação
        response = await api.post('/api/prompt', {
          name: xptoName,
          idOrganization: organizationId,
          prompt: xptoInstructions
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setSuccessAction('created');
      }
      
      console.log('Rule saved successfully:', response.data);
      
      // Mostrar modal de sucesso
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Error saving rule:', error);
      alert(t('form.rule.saveError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    
    // Salvar no localStorage para mostrar a mensagem na lista
    localStorage.setItem('@App:showSuccessModal', 'true');
    localStorage.setItem('@App:successAction', successAction);
    
    // Após salvar com sucesso, volta para a página de listagem
    navigate('/rules');
  };

  const handleBack = () => {
    navigate('/rules');
  };

  // Renderiza o formulário XPTO
  return (
    <Container>
      <Header>
        <Tooltip text={t('form.rule.backToRules')}>
          <BackButton onClick={handleBack}>
            {FiArrowLeft({ className: "icon", size: 30 })}
          </BackButton>
        </Tooltip>
      </Header>
      <FormWrapper>
        <XPTOContainer>
          <XPTOTitle>{id ? t('form.rule.editRule') : t('form.rule.addRule')}</XPTOTitle>
          <XPTODescription>
            {t('form.rule.instructionsDescription')}
          </XPTODescription>
          
          <InputGroup style={{ marginBottom: '1rem' }}>
            <Label htmlFor="xptoName">{t('form.rule.ruleName')}</Label>
            <Input 
              id="xptoName"
              name="xptoName"
              value={xptoName}
              onChange={handleXPTONameChange}
              placeholder={t('form.rule.ruleNamePlaceholder')}
              required
            />
          </InputGroup>
          
          <XPTOTextArea 
            value={xptoInstructions}
            onChange={handleXPTOInstructionsChange}
            placeholder={t('form.rule.instructionsPlaceholder')}
          />
          <XPTOSaveButton onClick={handleXPTOSubmit} disabled={isLoading}>
            {isLoading ? t('form.rule.saving') : t('form.rule.save')}
          </XPTOSaveButton>
        </XPTOContainer>
      </FormWrapper>
      
      {/* Modal de sucesso */}
      {showSuccessModal && (
        <AnimatedModal>
          <AnimatedModalContent>
            <h2>{t('common.success')}!</h2>
            <p>{successAction === 'created' ? t('form.rule.successCreated') : t('form.rule.successUpdated')}</p>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <BlueButton onClick={handleSuccessClose}>OK</BlueButton>
            </div>
          </AnimatedModalContent>
        </AnimatedModal>
      )}
    </Container>
  );
};

export default RuleForm; 